<template>
  <div>
    <div class="box">
      <loading v-if="isLoading"/>

      <div v-if="!isLoading">
        <div class="columns">
          <div class="column">
            <h2 class="title has-text-primary">
              <fa-icon :icon="faUserCog" class="margin-right-2"/>
              Users
            </h2>
          </div>
          <div class="column is-narrow">
            <div class="buttons">
              <custom-button :icon="faSyncAlt" iconPosition="left" modifier="primary" @click="refresh">Refresh
              </custom-button>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel-heading">
            <div class="columns">
              <div class="column">
                <div class="margin-top-1">Filter Options</div>
              </div>
              <div class="column margin-top-1 is-narrow">{{ filteredUsers.length }} result(s)</div>

              <div class="column is-narrow">
                <button class="button is-primary is-outlined" @click="resetFilters">
                  <fa-icon :icon="faUndo"/>
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
          <div class="panel-block">
            <p class="control has-icons-left">
              <input v-model="searchTerm" class="input" placeholder="Search..."/>
              <span class="icon is-left">
                <fa-icon :icon="faSearch"/>
              </span>
            </p>
          </div>
          <div class="panel-block">
            <p class="control has-icons-left">
              <multiselect v-model="searchUserGroup" :options="userGroups" label="title"
                           placeholder="Select a usergroup..." track-by="title">
                <span slot="noResult">No results found.</span>
              </multiselect>
            </p>
          </div>
        </div>

        <table v-if="filteredUsers.length > 0" class="table is-striped is-hoverable is-fullwidth">
          <thead>
          <tr>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Email</th>
            <th>Group</th>
            <th class="is-narrow"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(user, index) in filteredUsers" :key="index">
            <td>{{ user.firstName }}</td>
            <td>{{ user.lastName }}</td>
            <td>{{ user.emailAddress }}</td>
            <td>{{ user.userGroupTitle }}</td>
            <td class="is-narrow">
              <div class="buttons is-pulled-right">
                <custom-button :icon="faUserEdit" iconPosition="left" modifier="primary" @click="editUser(user)">Edit
                </custom-button>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
        <div v-if="users.length === 0" class="has-text-centered">No users found.</div>
        <div v-if="users.length > 0 && filteredUsers.length === 0" class="has-text-centered">No matching users found.
        </div>
      </div>
    </div>

    <modal v-if="showEditUserModal" :overflow="true"
           :title="showWorkingHoursEditor ? 'Manage working hours for ' + userBeingEdited.firstName + ' ' + userBeingEdited.lastName : 'Edit User'"
           @close="cancelEditingUser">
      <div v-show="!showWorkingHoursEditor">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Email</label>
          </div>
          <div class="field-body">
            <div class="field">
              <p class="control">
                <input :value="userBeingEdited.emailAddress" class="input is-static" readonly type="email"/>
              </p>
            </div>
          </div>
        </div>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">First Name</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input v-model="$v.userBeingEdited.firstName.$model"
                       :class="{'is-danger' : $v.userBeingEdited.$dirty && $v.userBeingEdited.firstName.$anyError}"
                       class="input" type="text"/>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.firstName.required"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.firstName.$params.required.msg }}
                </div>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.firstName.maxLength"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.firstName.$params.maxLength.msg }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Last Name</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input v-model="$v.userBeingEdited.lastName.$model"
                       :class="{'is-danger' : $v.userBeingEdited.$dirty && $v.userBeingEdited.lastName.$anyError}"
                       class="input" type="text"/>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.lastName.required"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.lastName.$params.required.msg }}
                </div>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.lastName.maxLength"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.lastName.$params.maxLength.msg }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Job Title</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input v-model="$v.userBeingEdited.jobTitle.$model"
                       :class="{'is-danger' : $v.userBeingEdited.$dirty && $v.userBeingEdited.jobTitle.$anyError}"
                       class="input" type="text"/>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.jobTitle.required"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.jobTitle.$params.required.msg }}
                </div>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.jobTitle.maxLength"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.jobTitle.$params.maxLength.msg }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="field is-horizontal margin-bottom-3">
          <div class="field-label is-normal">
            <label class="label">Group</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <multiselect v-model="$v.userBeingEdited.userGroup.$model" :options="userGroups"
                             label="title" placeholder="Type to search..." track-by="title">
                  <span slot="noResult">No results found.</span>
                </multiselect>
                <div v-if="$v.userBeingEdited.$dirty && !$v.userBeingEdited.userGroup.required"
                     class="help has-text-weight-semibold has-text-danger">
                  {{ $v.userBeingEdited.userGroup.$params.required.msg }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="field is-horizontal margin-bottom-3">
          <div class="field-label is-normal pt-0">
            <label class="label">Active?</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input v-model="userBeingEdited.isActive" type="checkbox"/>
              </div>
            </div>
          </div>
        </div>

        <div class="field is-horizontal margin-bottom-2">
          <div class="field-label is-normal">
            <label class="label"></label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <custom-button :icon="faHourglassHalf" iconPosition="left" modifier="primary"
                               @click="showWorkingHoursEditor = true">Edit Working Hours
                </custom-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="showWorkingHoursEditor">
        <div v-for="(v, index) in $v.userBeingEdited.workingHours.$each.$iter" :key="index" class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">{{ userBeingEdited.workingHours[index].dayOfWeekName }}</label>
          </div>
          <div class="field-body">
            <div class="columns">
              <div class="column is-narrow">
                <div class="field">
                  <div class="control">
                    <input v-model="userBeingEdited.workingHours[index].isEnabled" class="working-hours-checkbox"
                           type="checkbox"/>
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <div class="control">
                    <div :class="{'is-danger' : $v.userBeingEdited.$dirty && v.startTime.$anyError}"
                         :disabled="!userBeingEdited.workingHours[index].isEnabled"
                         class="input">
                      <datetime v-model="v.startTime.$model" :disabled="!userBeingEdited.workingHours[index].isEnabled"
                                :phrases="dateTimePhrases"
                                type="time"></datetime>
                    </div>
                  </div>
                  <div v-if="$v.userBeingEdited.workingHours.$dirty && !v.startTime.required"
                       class="help has-text-weight-semibold has-text-danger">
                    {{ v.startTime.$params.required.msg }}
                  </div>
                </div>
              </div>
              <div class="column is-narrow">
                <div class="margin-top-1">to</div>
              </div>
              <div class="column">
                <div class="field">
                  <div class="control">
                    <div :class="{'is-danger' : $v.userBeingEdited.$dirty && v.finishTime.$anyError}"
                         :disabled="!userBeingEdited.workingHours[index].isEnabled"
                         class="input">
                      <datetime v-model="v.finishTime.$model" :disabled="!userBeingEdited.workingHours[index].isEnabled"
                                :phrases="dateTimePhrases"
                                type="time"></datetime>
                    </div>
                  </div>
                  <div v-if="$v.userBeingEdited.workingHours.$dirty && !v.finishTime.required"
                       class="help has-text-weight-semibold has-text-danger">
                    {{ v.finishTime.$params.required.msg }}
                  </div>
                  <div v-if="$v.userBeingEdited.workingHours.$dirty && !v.finishTime.afterStartTime"
                       class="help has-text-weight-semibold has-text-danger">
                    {{ v.finishTime.$params.afterStartTime.msg }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template slot="buttons">
        <template v-if="!showWorkingHoursEditor">
          <custom-button class="inline" modifier="primary" @click="saveEditedUser">Submit</custom-button>
          <custom-button class="inline margin-left-2" @click="cancelEditingUser">Cancel</custom-button>
        </template>
        <template v-else>
          <custom-button :icon="faChevronLeft" iconPosition="left" modifier="primary" @click="hideWorkingHoursEditor">
            Back
          </custom-button>
        </template>
      </template>
    </modal>
  </div>
</template>

<script>
import loading from "@/components/loading"
import customButton from "@/components/custom-button"
import modal from "@/components/modal"
import Multiselect from 'vue-multiselect'
import {
  faChevronLeft,
  faHourglassHalf,
  faSearch,
  faSyncAlt,
  faUndo,
  faUserCog,
  faUserEdit
} from "@fortawesome/free-solid-svg-icons"
import {shallowCopy} from "@/utilities"
import {withParams} from 'vuelidate';
import {maxLength, required} from "vuelidate/lib/validators";

export default {
  components: {
    loading,
    customButton,
    modal,
    Multiselect
  },
  computed: {
    filteredUsers() {
      var results = this.users;

      if (this.searchTerm !== "") {
        results = results.filter(x => x.firstName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            x.lastName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
            x.emailAddress.toLowerCase().includes(this.searchTerm.toLowerCase()))
      }

      if (this.searchUserGroup !== null) {
        results = results.filter(x => x.userGroupId == this.searchUserGroup.id)
      }

      return results;
    }
  },
  data() {
    return {
      isLoading: true,
      users: [],
      userGroups: null,
      searchTerm: "",
      searchUserGroup: null,
      userBeingEdited: {
        id: null,
        userGroup: null,
        userGroupId: null,
        firstName: null,
        lastName: null,
        emailAddress: null,
        jobTitle: null,
        workingHours: [],
        isActive: true
      },
      showEditUserModal: false,
      showWorkingHoursEditor: false,
      faUserCog,
      faSyncAlt,
      faUserEdit,
      faSearch,
      faUndo,
      faHourglassHalf,
      faChevronLeft,
      dateTimePhrases: {
        ok: "OK",
        cancel: "Cancel"
      }
    }
  },
  methods: {
    refresh() {
      this.isLoading = true

      this.$http.get("Users").then(response => {
        this.users = response.data

        this.$http.get("UserGroups").then(response => {
          this.userGroups = response.data
          this.isLoading = false
        }).catch(() => {
          this.isLoading = false
          this.$alert("An error occurred loading the user groups.", "", "error");
        })
      }).catch(() => {
        this.isLoading = false
        this.$alert("An error occurred loading the users.", "", "error");
      })
    },
    editUser(user) {
      this.$v.userBeingEdited.$reset();

      var copy = shallowCopy(user);

      copy.userGroup = null;

      if (copy.userGroupId) {
        copy.userGroup = this.userGroups.filter(x => x.id == copy.userGroupId)[0];
      }

      this.userBeingEdited = copy;
      this.showWorkingHoursEditor = false;
      this.showEditUserModal = true;
    },
    saveEditedUser() {
      this.$v.userBeingEdited.$touch();

      if (this.$v.userBeingEdited.$invalid)
        return;

      this.isLoading = true

      var data = {
        id: this.userBeingEdited.id,
        userGroupId: this.userBeingEdited.userGroup.id,
        firstName: this.userBeingEdited.firstName,
        lastName: this.userBeingEdited.lastName,
        jobTitle: this.userBeingEdited.jobTitle,
        workingHours: this.userBeingEdited.workingHours,
        isActive: this.userBeingEdited.isActive
      };

      this.$http.post("Users", data).then(() => {
        this.showEditUserModal = false;
        this.refresh();
        this.$alert("User updated successfully.", "", "success");
      }).catch((error) => {
        if (error.response.data.message) {
          this.$alert(error.response.data.message, "", "error");
        } else this.$alert("An error occurred updating the user.", "", "error");

        this.isLoading = false
      });
    },
    cancelEditingUser() {
      this.showEditUserModal = false;
    },
    resetFilters() {
      this.searchTerm = "";
      this.searchUserGroup = null;
    },
    hideWorkingHoursEditor() {
      this.$v.userBeingEdited.workingHours.$touch();

      if (this.$v.userBeingEdited.workingHours.$invalid)
        return;

      this.showWorkingHoursEditor = false;
    }
  },
  created() {
    this.refresh()
  },
  validations: {
    userBeingEdited: {
      firstName: {
        required: withParams({msg: "Please enter a first name."}, required),
        maxLength: withParams({msg: "The first name must be no longer than 50 characters."}, maxLength(50))
      },
      lastName: {
        required: withParams({msg: "Please enter a last name."}, required),
        maxLength: withParams({msg: "The last name must be no longer than 50 characters."}, maxLength(50))
      },
      jobTitle: {
        required: withParams({msg: "Please enter a job title."}, required),
        maxLength: withParams({msg: "The job title must be no longer than 50 characters."}, maxLength(50))
      },
      userGroup: {
        required: withParams({msg: "Please select a group."}, required)
      },
      workingHours: {
        $each: {
          startTime: {
            required: withParams({msg: "Please provide a start time."}, (value, workingHours) => {
              if (workingHours.isEnabled)
                return required(value, workingHours);

              return true;
            })
          },
          finishTime: {
            required: withParams({msg: "Please provide a finish time."}, (value, workingHours) => {
              if (workingHours.isEnabled)
                return required(value, workingHours);

              return true;
            }),
            afterStartTime: withParams({msg: "The finish time must be after the start time."}, function (value, workingHours) {
              if (workingHours.isEnabled && value !== "" && workingHours.startTime !== "") {
                return this.$moment(value).isAfter(workingHours.startTime);
              }

              return true;
            })
          }
        }
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.working-hours-checkbox
  margin-top: 13px
</style>